function num(name: string, fallback: number): number {
  const raw = process.env[name];
  if (!raw) return fallback;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : fallback;
}

/** Railway (and most hosts) inject PORT; local/dev uses BACKEND_PORT. */
const backendPort = num("PORT", num("BACKEND_PORT", 4000));

/** Public product identity — used in UI, OTP copy, titles, and marketing. */
export const branding = {
  /** Wordmark as shown in the logo (no space). */
  name: "SendCollab",
  domain: "sendcollab.me",
  /** Single-letter mark in the logo chip. */
  mark: "S",
} as const;

export const config = {
  appUrl: process.env.APP_URL ?? "http://localhost:3000",
  backendPort,
  /** Direct URL to the API process. Defaults from BACKEND_PORT when unset. */
  backendUrl: process.env.BACKEND_URL ?? `http://localhost:${backendPort}`,
  /**
   * Session cookie Domain for cross-subdomain auth
   * (e.g. `.sendcollab.me` so sendcollab.me + api.sendcollab.me share it).
   * Leave unset for localhost (host-only cookie).
   */
  cookieDomain: (process.env.COOKIE_DOMAIN ?? "").trim(),
  authSecret: process.env.AUTH_SECRET ?? "insecure-dev-secret",
  cronSecret: process.env.CRON_SECRET ?? "",
  /** Admin sign-in phones, E.164 (`+91…`). Comma-separated. */
  adminPhones: (process.env.ADMIN_PHONES ?? "")
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean),

  /**
   * Meta WhatsApp Cloud API (fallback). Prefer Twilio WhatsApp below when
   * TWILIO_WHATSAPP_FROM is set. When neither is configured, messages are
   * logged to the server console (OTP codes included for local login).
   */
  whatsapp: {
    token: process.env.WHATSAPP_TOKEN ?? "",
    phoneNumberId: process.env.WHATSAPP_PHONE_NUMBER_ID ?? "",
  },

  /**
   * Razorpay for paid offer boosts (Serious / Priority). When unset, only
   * Basic (free) offers can be submitted.
   */
  razorpay: {
    keyId: process.env.RAZORPAY_KEY_ID ?? "",
    keySecret: process.env.RAZORPAY_KEY_SECRET ?? "",
    webhookSecret: process.env.RAZORPAY_WEBHOOK_SECRET ?? "",
  },

  /**
   * Twilio — Voice (proxy calls) + WhatsApp (OTP + collab notifications).
   * Voice: SID + token enough; caller ID auto-picks any Voice number.
   * WhatsApp: also set TWILIO_WHATSAPP_FROM (`whatsapp:+…` sandbox or sender).
   */
  twilio: {
    accountSid: process.env.TWILIO_ACCOUNT_SID ?? "",
    authToken: process.env.TWILIO_AUTH_TOKEN ?? "",
    /**
     * Optional. Any Voice number on the account works as caller ID; when unset
     * we pick the first number from the Twilio account automatically.
     */
    phoneNumber: process.env.TWILIO_PHONE_NUMBER ?? "",
    /**
     * Public origin Twilio hits for TwiML / status / recording webhooks
     * (must be HTTPS and reachable from the internet). Falls back to APP_URL.
     * Locally: ngrok http 3000 → set this to the https://….ngrok URL.
     */
    webhookBase: process.env.TWILIO_WEBHOOK_BASE ?? "",
    /**
     * WhatsApp sender, e.g. `whatsapp:+14155238886` (sandbox) or your
     * approved business sender. Required to deliver OTP / notifications.
     */
    whatsappFrom: process.env.TWILIO_WHATSAPP_FROM ?? "",
    /**
     * Twilio Content SID for the OTP authentication template
     * (`whatsapp/authentication`). Required for business-initiated OTP.
     */
    whatsappOtpContentSid: process.env.TWILIO_WHATSAPP_OTP_CONTENT_SID ?? "",
    /**
     * Twilio Content SID for collab status updates (utility text template).
     * Variables: {{1}} title, {{2}} detail, {{3}} open URL.
     */
    whatsappUpdateContentSid: process.env.TWILIO_WHATSAPP_UPDATE_CONTENT_SID ?? "",
  },

  /**
   * Uploaded files. With a bucket named, objects go to S3; without one they go
   * into Postgres, so a clone of this repo works with nothing but a database.
   * Credentials come from the usual AWS chain — env, profile, or instance role.
   */
  storage: {
    bucket: process.env.S3_BUCKET ?? "",
    region: process.env.S3_REGION ?? process.env.AWS_REGION ?? "ap-south-1",
    /** Keeps app objects in their own namespace inside a shared bucket. */
    prefix: process.env.S3_PREFIX ?? "",
    /** Set for S3-compatible services such as MinIO or R2. */
    endpoint: process.env.S3_ENDPOINT ?? "",
  },

  timers: {
    offerExpiryDays: num("OFFER_EXPIRY_DAYS", 7),
    /** How long the payer has to send the advance before the collab expires. */
    advanceWindowDays: num("ADVANCE_WINDOW_DAYS", 3),
    draftReviewDays: num("DRAFT_REVIEW_DAYS", 3),
    publishConfirmDays: num("PUBLISH_CONFIRM_DAYS", 3),
    livePeriodDays: num("LIVE_PERIOD_DAYS", 60),
  },
} as const;

export const DEFAULT_ADVANCE_PCT = 25;
